/* Pull the page down to fetch fresh marks.
 *
 * The app already refreshes on its own cadence and whenever it comes back to
 * the front, so this is not how prices arrive — it is how you settle the
 * question "is this actually current?" without waiting to find out. On a phone
 * that gesture is a reflex, and its absence reads as the app being stuck.
 *
 * Three things it deliberately does not do:
 *
 *   - It does not fire from inside a chart. The value chart is scrubbed by
 *     dragging across it, and a drag that starts there means "show me that
 *     day", never "reload". Anything marked `data-no-pull` is left alone.
 *   - It does not fight the scroll. The gesture only starts at the very top of
 *     the page, and only once the finger has clearly gone downwards — a
 *     diagonal flick to scroll is not a pull.
 *   - It does not pretend. The indicator says what it will do at the distance
 *     you are actually at, and if the fetch fails the failure surfaces where
 *     every other refresh failure does rather than being swallowed here. */

import { useEffect, useRef, useState } from 'react';
import { useStore } from '../state/store';

/** How far to pull before it triggers. Far enough that a scroll cannot do it by
    accident, near enough that it never feels like a fight. */
const TRIGGER_PX = 72;
/** The pull is damped: the finger moves further than the page does, which is
    what makes the end of the travel feel like a limit rather than a stop. */
const DAMPING = 0.5;
const MAX_PX = 96;

export function PullToRefresh({ children }: { children: React.ReactNode }) {
  const { refresh, state } = useStore();
  /* The distance lives outside React. It changes on every frame of a pull, and
     held as state each of those frames went through a render to move two
     elements. Written straight to them, a frame of pulling is a style change
     and nothing more. React hears only about the moments the words change. */
  const indicator = useRef<HTMLDivElement | null>(null);
  const content = useRef<HTMLDivElement | null>(null);
  const [phase, setPhase] = useState<'idle' | 'pulling' | 'ready'>('idle');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  busyRef.current = busy;

  useEffect(() => {
    const atTop = () => (document.scrollingElement?.scrollTop ?? window.scrollY) <= 0;
    let start: { y: number; armed: boolean } | null = null;
    let distance = 0;
    let animate = false;
    let frame: number | null = null;

    /** At most one write per frame, whatever the finger reports. */
    const paint = (px: number, withEase: boolean) => {
      distance = px;
      animate = withEase;
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const ind = indicator.current;
        const page = content.current;
        if (ind) {
          ind.style.height = `${distance}px`;
          ind.style.opacity = distance > 4 ? '1' : '0';
        }
        if (page) {
          page.style.transition = animate ? 'transform 180ms ease-out' : 'none';
          page.style.transform = distance ? `translateY(${distance}px)` : '';
        }
      });
    };

    const settle = () => { paint(0, true); setPhase('idle'); };

    const onMove = (e: TouchEvent) => {
      if (!start || busyRef.current) return;
      const dy = e.touches[0]!.clientY - start.y;

      // Upwards, or the page has been scrolled since: this is a scroll.
      if (dy <= 0 || !atTop()) { stopListening(); settle(); return; }
      if (!start.armed && dy < 8) return;
      start.armed = true;

      /* Taking the gesture means the page must not also scroll with it, or the
         indicator and the content move at once and neither reads. */
      if (e.cancelable) e.preventDefault();
      const px = Math.min(MAX_PX, dy * DAMPING);
      paint(px, false);
      setPhase(px >= TRIGGER_PX ? 'ready' : 'pulling');
    };

    /* The move listener has to be able to block the scroll, and a listener that
       can block makes the browser wait for it before every scroll. So it is
       attached only for a touch that begins at the very top — the only place a
       pull can start — and taken away the moment that touch turns out to be a
       scroll or ends. Everywhere else the page scrolls without asking. */
    const stopListening = () => {
      window.removeEventListener('touchmove', onMove);
      start = null;
    };

    const onStart = (e: TouchEvent) => {
      if (busyRef.current || !atTop() || e.touches.length !== 1) return;
      /* Not every touch target is an element — a synthetic one can be the window
         itself — and a throw here would take the listener down for the rest of
         the session, leaving the gesture dead with nothing on screen to say so. */
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('.chart-wrap, [data-no-pull], input, textarea, select')) return;
      start = { y: e.touches[0]!.clientY, armed: false };
      window.addEventListener('touchmove', onMove, { passive: false });
    };

    const onEnd = () => {
      const from = start;
      stopListening();
      if (!from?.armed || distance < TRIGGER_PX || busyRef.current) { settle(); return; }

      paint(TRIGGER_PX, true);
      setBusy(true);
      void refresh({ full: true }).finally(() => { setBusy(false); settle(); });
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [refresh]);

  const running = busy || state.refreshing;

  return (
    <>
      {/* Height and opacity are written by the gesture above; these are only the
          values it starts from, and they do not change between renders, so a
          render never undoes a pull in progress. */}
      <div ref={indicator} className="pull-indicator" style={{ height: 0, opacity: 0 }}
           aria-hidden={phase === 'idle' && !running}>
        <span className="pull-label">
          {running ? 'Fetching marks…' : phase === 'ready' ? 'Release to refresh' : 'Pull to refresh'}
        </span>
      </div>
      <div ref={content}>
        {children}
      </div>
    </>
  );
}
